import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { apiSync } from "@/lib/api";

// Environment detection
const isProduction = (): boolean => {
  const hostname = window.location.hostname;
  return hostname !== "localhost" && !hostname.includes("lovable") && !hostname.includes("webcontainer");
};

const Contact = () => {
  const location = useLocation();
  const isBezoekRoute = location.pathname === "/bezoek";
  const settings = apiSync.getSiteSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const { toast } = useToast();

  // Default values while loading
  const contact = settings?.contact || {
    email: "alpacadordrecht@gmail.com",
    phone: "0782200022",
    phoneDisplay: "078-22 000 22",
    address: "Noordendijk 406, 3319GM Dordrecht",
  };

  const openingHours = settings?.openingHours || "Woensdag t/m zondag\n11:00 - 17:00";
  const openingHoursLines = openingHours.split("\n");

  const openingHoursNoteContact =
    settings?.openingHoursNoteContact || "Alleen op reservering\nReserveer minimaal 24 uur van tevoren";
  const contactNoteLines = openingHoursNoteContact.split("\n");

  const reservationInfo =
    settings?.reservationInfo ||
    "Reserveren kan maximaal 24 uur van tevoren\nVoor wijzigingen of annuleringen kun je via email contact opnemen tot maximaal 2 dagen van tevoren";
  const reservationInfoLines = reservationInfo.split("\n");

  const defaultContactFaqText =
    "Heb je een vraag over reserveren, dieetwensen of bereikbaarheid? Bekijk dan eerst onze veelgestelde vragen — misschien staat het antwoord er al tussen!";
  const contactFaqText = settings?.contactFaqText ?? defaultContactFaqText;
  const showContactFaqReference = contactFaqText.trim() !== "";
  const phoneCardEnabled = settings?.phoneCardEnabled !== false;

  // Generate and set CSRF token cookie on mount
  useEffect(() => {
    if (!document.cookie.includes("csrf_token=")) {
      const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");
      document.cookie = `csrf_token=${token}; path=/; SameSite=Strict; Secure`;
    }
  }, []);

  const getCsrfToken = (): string => {
    const match = document.cookie.match(/csrf_token=([^;]+)/);
    return match ? match[1] : "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const csrfToken = getCsrfToken();
      if (!csrfToken) {
        throw new Error("Beveiligingstoken ontbreekt. Vernieuw de pagina en probeer opnieuw.");
      }

      const res = await fetch("/api/sendmail.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          website: honeypot,
          csrf_token: csrfToken,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error("Server gaf een ongeldig antwoord. Probeer het later opnieuw.");
      }

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Versturen mislukt. Controleer je gegevens en probeer het opnieuw.");
      }

      toast({
        title: "Bericht verzonden",
        description: "We nemen zo snel mogelijk contact met je op.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Er ging iets mis. Probeer het opnieuw.";

      toast({
        title: "Versturen mislukt",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = isBezoekRoute
    ? "Plan je Bezoek | Alpacaboerderij Dordrecht"
    : "Contact & Reserveren | Alpacaboerderij Dordrecht";

  const pageDescription = isBezoekRoute
    ? "Reserveer je bezoek aan Alpacaboerderij Dordrecht. Kies tussen alpacapicknick of high tea. Woensdag t/m zondag geopend."
    : "Plan je bezoek aan Alpacaboerderij Dordrecht. Noordendijk 406, 3319GM Dordrecht. Open woensdag t/m zondag, alleen op reservering.";

  const canonical = isBezoekRoute ? "/bezoek/" : "/contact/";

  // Local Business structured data
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Alpacaboerderij Dordrecht",
    image: "https://alpacaboerderijdordrecht.nl/assets/hero-alpacas.webp",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Noordendijk 406",
      addressLocality: "Dordrecht",
      postalCode: "3319GM",
      addressCountry: "NL",
    },
    telephone: contact.phoneDisplay,
    email: contact.email,
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "11:00",
      closes: "17:00",
    },
    priceRange: "€€",
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead title={pageTitle} description={pageDescription} canonical={canonical} />

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema).replace(/</g, "\\u003c"),
        }}
      />

      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section
          className="bg-gradient-to-br from-primary/10 via-secondary/20 to-background py-20"
          aria-labelledby="contact-hero-heading"
        >
          <div className="container text-center">
            <h1
              id="contact-hero-heading"
              className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in motion-reduce:animate-none"
            >
              Contact
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Neem contact met ons op als je een vraag of opmerking hebt.
            </p>
          </div>
        </section>

        {/* FAQ Reference */}
        {showContactFaqReference && (
          <section className="pt-20 pb-10">
            <div className="container max-w-4xl">
              <aside className="text-center p-8 bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl">
                <h3 className="text-2xl font-bold mb-4">Misschien al beantwoord!</h3>
                <p className="text-lg text-muted-foreground mb-6">{contactFaqText}</p>
                <Link to="/faq" className="text-primary font-semibold hover:underline">
                  Bekijk de veelgestelde vragen →
                </Link>
              </aside>
            </div>
          </section>
        )}

        {/* Contact Section */}
        <section className="py-20" aria-labelledby="contact-section-heading">
          <h2 id="contact-section-heading" className="sr-only">
            Contactinformatie en formulier
          </h2>
          <div className="container max-w-6xl">
            <div className="grid gap-12 lg:grid-cols-2">
              {/* Contact Info */}
              <div className="space-y-8">
                <header>
                  <h3 className="text-3xl font-bold mb-6">Neem contact op</h3>
                  <p className="text-lg text-muted-foreground">
                    We horen graag van je! Stuur ons een bericht of neem direct contact op via onderstaande gegevens.
                  </p>
                </header>

                <address className="space-y-6 not-italic">
                  <Card className="p-6 hover-lift">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Mail size={24} aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Email</h4>
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {contact.email}
                        </a>
                      </div>
                    </div>
                  </Card>

                  {phoneCardEnabled && (
                  <Card className="p-6 hover-lift">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Phone size={24} aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Telefoon</h4>
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {contact.phoneDisplay}
                        </a>
                        <p className="text-xs text-muted-foreground mt-1">Alleen bereikbaar binnen openingstijden</p>
                      </div>
                    </div>
                  </Card>
                  )}

                  <Card className="p-6 hover-lift">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <MapPin size={24} aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Locatie</h4>
                        <p className="text-muted-foreground">{contact.address}</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 hover-lift">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Clock size={24} aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Openingstijden</h4>
                        <div className="text-muted-foreground">
                          {openingHoursLines.map((line, index) => (
                            <p key={index}>{line || "\u00A0"}</p>
                          ))}
                        </div>
                        {contactNoteLines.length > 0 && (
                          <div className="text-muted-foreground mt-2 text-sm italic">
                            {contactNoteLines.map((line, index) => (
                              <p key={index}>{line || "\u00A0"}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </address>
              </div>

              {/* Contact Form */}
              <Card className="p-8">
                <h3 className="text-2xl font-bold mb-6">Stuur een bericht</h3>
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {/* Honeypot field - hidden from users */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website (laat leeg)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Naam <span aria-label="verplicht">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      required
                      minLength={2}
                      maxLength={100}
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                      placeholder="Jouw naam"
                      autoComplete="name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email <span aria-label="verplicht">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={255}
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                      placeholder="jouw@email.nl"
                      autoComplete="email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Telefoon
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      maxLength={20}
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phone: e.target.value,
                        })
                      }
                      placeholder="+31 6 12 34 56 78"
                      autoComplete="tel"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Bericht <span aria-label="verplicht">*</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      minLength={10}
                      maxLength={5000}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          message: e.target.value,
                        })
                      }
                      placeholder="Vertel ons over je bezoek, aantal personen, dieetwensen, etc."
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Verzenden..." : "Verstuur bericht"}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </section>

        {/* Important Info */}
        <section className="py-20 bg-muted/30" aria-labelledby="reservation-info-heading">
          <div className="container max-w-4xl">
            <Card className="p-8">
              <h3 id="reservation-info-heading" className="text-2xl font-bold mb-6">
                Reserveringsinformatie
              </h3>
              <ul className="space-y-4 text-muted-foreground" role="list">
                {reservationInfoLines.map((line, index) =>
                  line.trim() ? (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary" aria-hidden="true">
                        ✓
                      </span>
                      <span>{line}</span>
                    </li>
                  ) : (
                    <li key={index} className="h-4" aria-hidden="true"></li>
                  ),
                )}
              </ul>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
